import { useRouter } from 'next/router';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { AccessCodeState, SponsorCodeState } from './types';

export const accessCodeQueryString = 'access_code';
export const sponsorCodeQueryString = 'sponsor_code';

export const AccessCodeContext = createContext<AccessCodeState>(undefined!);
export const SponsorCodeContext = createContext<SponsorCodeState>(undefined!);

export function MagicLinkProvider({ children }: PropsWithChildren) {
    const { query, replace, pathname, isReady } = useRouter();
    const {
        [accessCodeQueryString]: queryAccessCode,
        [sponsorCodeQueryString]: querySponsorCode,
    } = query;

    // State is the primary source for the access code. The query param is only
    // used as a fallback (e.g., on first load from a magic link URL).
    const [stateAccessCode, setStateAccessCode] = useState<string>('');

    // Resolves access code: state takes priority over query param.
    const accessCode = useMemo(
        () =>
            stateAccessCode
                ? stateAccessCode
                : queryAccessCode
                ? (queryAccessCode as string)
                : '',
        [queryAccessCode, stateAccessCode]
    );

    const setAccessCode = useCallback(
        (code: string, willSetQuery = false) => {
            setStateAccessCode(code);
            if (willSetQuery) {
                void replace(
                    {
                        pathname,
                        query: { ...query, [accessCodeQueryString]: code },
                    },
                    undefined,
                    { shallow: true }
                );
            }
        },
        [pathname, query, replace]
    );

    const removeAccessCode = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [accessCodeQueryString]: _removed, ...rest } = query;
        void replace({ pathname, query: rest }, undefined, { shallow: true });
        setStateAccessCode('');
    }, [pathname, query, replace]);

    // State, once set, wins over the query param — the same override
    // MagicLinkProvider gives accessCode. removeSponsorCode's own
    // query-stripping replace() is async; a caller that needs the clear to
    // be visible to a synchronous read before that replace lands (e.g. a
    // click handler racing an in-flight navigation) can call setSponsorCode
    // directly instead.
    const [stateSponsorCode, setStateSponsorCode] = useState<string | null>(
        null
    );
    const sponsorCode =
        stateSponsorCode !== null
            ? stateSponsorCode
            : querySponsorCode
            ? (querySponsorCode as string)
            : '';

    const setSponsorCode = useCallback((code: string) => {
        setStateSponsorCode(code);
    }, []);

    const removeSponsorCode = useCallback(() => {
        setStateSponsorCode('');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [sponsorCodeQueryString]: _removed, ...rest } = query;
        void replace({ pathname, query: rest }, undefined, { shallow: true });
    }, [pathname, query, replace]);

    return (
        <AccessCodeContext.Provider
            value={{
                isReady,
                accessCode,
                hasAccessCode: !!accessCode,
                isAccessCodeFromQuery:
                    !!accessCode && queryAccessCode === accessCode,
                setAccessCode,
                removeAccessCode,
            }}
        >
            <SponsorCodeContext.Provider
                value={{
                    sponsorCode,
                    hasSponsorCode: !!sponsorCode,
                    setSponsorCode,
                    removeSponsorCode,
                }}
            >
                {children}
            </SponsorCodeContext.Provider>
        </AccessCodeContext.Provider>
    );
}
